import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, Users, DollarSign, TrendingUp, CheckCircle2, 
  Star, Award, Zap, ArrowRight, Clock, ChevronRight,
  Building2, Phone, Mail, Globe, Instagram, MapPin,
  Share2, Download, Sparkles
} from 'lucide-react';
import SoldiersSocialCard from '@/components/SoldiersSocialCard';

export default function SoldiersPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [programStatus, setProgramStatus] = useState(null);
  const [myApplication, setMyApplication] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    salon_name: '',
    owner_name: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    num_chairs: 1,
    num_stylists: 1,
    years_in_business: 1,
    instagram_handle: '',
    facebook_url: '',
    tiktok_handle: '',
    proof_of_business_url: '',
    license_upload_url: '',
    why_join: '',
    referral_source: '',
    // Eligibility confirmations
    is_verified_owner: false,
    has_active_license: false,
    min_one_year: false,
    min_one_chair: false,
    has_social_presence: false,
    commits_ethical_recruiting: false
  });

  useEffect(() => {
    fetchProgramStatus();
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email, owner_name: user.name || '' }));
      fetchMyApplication();
    }
  }, [user]);

  const fetchProgramStatus = async () => {
    try {
      const response = await axios.get(`${API}/soldiers/program-status`);
      setProgramStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch program status:', error);
    }
  };

  const fetchMyApplication = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/soldiers/my-application`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyApplication(response.data);
    } catch (error) {
      console.error('Failed to fetch application:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/soldiers/apply`, formData);
      toast.success(response.data.message);
      setShowForm(false);
      fetchProgramStatus();
      if (token) fetchMyApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const spotsRemaining = programStatus?.spots_remaining || 50;
  const spotsFilled = programStatus?.spots_filled || 0;
  const progressPercent = (spotsFilled / 50) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-amber-400 transition-colors mb-8"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Home
          </button>

          {/* Main Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              FOUNDING PARTNER PROGRAM
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              <span className="text-white">HairChair</span>
              <span className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent"> Soldiers</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-zinc-400 max-w-3xl mx-auto mb-4">
              Join the elite 50 salon owners building the future of the beauty industry.
              <span className="text-amber-400"> Earn while you grow.</span>
            </p>

            {/* Marketing Share Button */}
            <div className="flex justify-center gap-3 mb-8">
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 gap-2"
                data-testid="share-soldiers-btn"
              >
                <Share2 className="w-4 h-4" />
                Share & Earn
              </Button>
              <Button
                onClick={() => setShowShareModal(true)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 gap-2"
                data-testid="download-marketing-card-btn"
              >
                <Download className="w-4 h-4" />
                Marketing Card
              </Button>
            </div>

            {/* Diverse Team Image */}
            <div className="max-w-4xl mx-auto mb-10 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/f35d2631-7989-4ade-a61d-13dc62e450ea/images/94ac6f334c423deb635538373cb4c16bec168b0672e6ab8e838eb0065e6ae3f0.png"
                alt="Diverse team of professional barbers and hair stylists holding styling tools"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Progress Bar */}
            <div className="max-w-md mx-auto mb-8">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-zinc-500">Founding Partners</span>
                <span className="text-amber-400 font-semibold">{spotsFilled} / 50 Spots Filled</span>
              </div>
              <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-1000"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-sm text-zinc-500 mt-2">
                {spotsRemaining > 0 
                  ? `${spotsRemaining} spots remaining - Apply now!`
                  : 'Program full - Join the waitlist'}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {myApplication?.has_application ? (
                myApplication.application?.status === 'approved' ? (
                  <Button 
                    size="lg"
                    onClick={() => navigate('/soldiers/dashboard')}
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-8 py-6 text-lg"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <div className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    Application Status: <span className="capitalize text-amber-400">{myApplication.application?.status}</span>
                  </div>
                )
              ) : (
                <Button 
                  size="lg"
                  onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-8 py-6 text-lg"
                  data-testid="apply-now-btn"
                >
                  Apply Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              )}
              <Button 
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-8 py-6 text-lg"
              >
                Learn More
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: '30-Day Free Trial', value: '$0', icon: Clock, desc: 'Your referrals start free' },
              { label: 'Months 1-2 Bonus', value: '$20-$30', icon: DollarSign, desc: 'Pro: $20 / Elite: $30' },
              { label: 'Lifetime Residual', value: '8%', icon: TrendingUp, desc: 'Ongoing forever' },
              { label: 'Founding Spots', value: '50', icon: Award, desc: 'Limited availability' }
            ].map((stat, i) => (
              <Card key={i} className="bg-zinc-900/50 border-zinc-800 hover:border-amber-500/30 transition-all group">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <stat.icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-amber-400 font-medium text-sm">{stat.label}</div>
                  <div className="text-zinc-500 text-xs mt-1">{stat.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The <span className="text-amber-400">Soldier Payout Engine</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Your referrals get 30 days FREE, then intro pricing for 3 months. You earn commissions starting from their first payment.
            </p>
          </div>

          {/* Visual Pricing Ladder */}
          <div className="mb-12 max-w-4xl mx-auto">
            <Card className="bg-zinc-950 border-zinc-800 overflow-hidden">
              <CardContent className="p-0">
                <div className="h-2 bg-gradient-to-r from-blue-500 via-amber-500 to-green-500" />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-4 text-center">Your Referral&apos;s Pricing Ladder</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="py-3 px-4 text-left text-zinc-400">Step</th>
                          <th className="py-3 px-4 text-left text-zinc-400">Timeframe</th>
                          <th className="py-3 px-4 text-center text-amber-400">Pro Plan</th>
                          <th className="py-3 px-4 text-center text-purple-400">Elite Plan</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-zinc-800/50 bg-blue-500/5">
                          <td className="py-3 px-4 text-blue-400 font-bold">Step 1</td>
                          <td className="py-3 px-4 text-zinc-300">First 30 Days</td>
                          <td className="py-3 px-4 text-center text-green-400 font-bold text-lg">FREE</td>
                          <td className="py-3 px-4 text-center text-green-400 font-bold text-lg">FREE</td>
                        </tr>
                        <tr className="border-b border-zinc-800/50 bg-amber-500/5">
                          <td className="py-3 px-4 text-amber-400 font-bold">Step 2</td>
                          <td className="py-3 px-4 text-zinc-300">Next 3 Months</td>
                          <td className="py-3 px-4 text-center text-white font-bold">$19.99/mo</td>
                          <td className="py-3 px-4 text-center text-white font-bold">$39.99/mo</td>
                        </tr>
                        <tr className="bg-green-500/5">
                          <td className="py-3 px-4 text-green-400 font-bold">Step 3</td>
                          <td className="py-3 px-4 text-zinc-300">Thereafter</td>
                          <td className="py-3 px-4 text-center text-zinc-300">$29.99/mo</td>
                          <td className="py-3 px-4 text-center text-zinc-300">$49.99/mo</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: '30-Day Free Trial',
                bonus: '$0 Payout',
                icon: Clock,
                desc: 'Your referral enjoys 30 days completely free. You earn nothing yet, but you\'re building loyalty.',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                step: '02',
                title: 'Month 1 Payment',
                bonus: '$10-$15 Held',
                icon: Users,
                desc: 'First payment at intro rate ($19.99 Pro / $39.99 Elite). Your bonus is added to PENDING status.',
                color: 'from-yellow-500 to-amber-500'
              },
              {
                step: '03',
                title: 'Month 2 Release',
                bonus: '$20-$30 Released',
                icon: CheckCircle2,
                desc: 'Second payment received. Month 1 + Month 2 bonus ($10+$10 Pro / $15+$15 Elite) is RELEASED!',
                color: 'from-green-500 to-emerald-500'
              },
              {
                step: '04',
                title: '8% Residual',
                bonus: 'Forever',
                icon: TrendingUp,
                desc: 'Month 3+: Earn 8% of every payment. Intro rate for month 3, then regular price month 4+.',
                color: 'from-purple-500 to-pink-500'
              }
            ].map((item, i) => (
              <Card key={i} className="bg-zinc-950 border-zinc-800 overflow-hidden group hover:border-zinc-700 transition-all">
                <CardContent className="p-0">
                  <div className={`h-2 bg-gradient-to-r ${item.color}`} />
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} bg-opacity-20 flex items-center justify-center`}>
                        <item.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <span className="text-zinc-500 text-sm font-mono">STEP {item.step}</span>
                        <h3 className="text-xl font-bold text-white">{item.title}</h3>
                      </div>
                    </div>
                    <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${item.color} text-black text-sm font-bold mb-3`}>
                      {item.bonus}
                    </div>
                    <p className="text-zinc-400">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Example Calculation */}
          <Card className="mt-12 bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/30">
            <CardContent className="p-6 sm:p-8">
              <h3 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Real Earning Example: 10 Elite Referrals
              </h3>
              <p className="text-zinc-300 mb-4">
                If you refer <strong>10 Elite subscribers</strong> who convert after their 30-day free trial:
              </p>
              <div className="grid sm:grid-cols-5 gap-4">
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-400">$0</div>
                  <div className="text-zinc-400 text-sm">Month 0 (Trial)</div>
                  <div className="text-zinc-600 text-xs">30 days free</div>
                </div>
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-400">$150</div>
                  <div className="text-zinc-400 text-sm">Month 1 (Held)</div>
                  <div className="text-zinc-600 text-xs">10 × $15 pending</div>
                </div>
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-400">$300</div>
                  <div className="text-zinc-400 text-sm">Month 2 (Released)</div>
                  <div className="text-zinc-600 text-xs">10 × $30 total</div>
                </div>
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-400">$32/mo</div>
                  <div className="text-zinc-400 text-sm">Month 3 (8%)</div>
                  <div className="text-zinc-600 text-xs">10 × $3.20 intro</div>
                </div>
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-amber-400">$40/mo</div>
                  <div className="text-zinc-400 text-sm">Month 4+ (8%)</div>
                  <div className="text-zinc-600 text-xs">10 × $4.00 regular</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <p className="text-green-300 font-bold text-lg">
                  💰 Year 1 Total: ~$780+ with just 10 Elite referrals!
                </p>
                <p className="text-zinc-400 text-sm mt-1">
                  $300 bonuses + $32 (month 3) + $360 (months 4-12) = $692 + ongoing growth
                </p>
              </div>
              <p className="text-zinc-500 text-sm mt-4">
                * Pro subscribers: $20 total bonus + ~$1.60/mo (month 3) + ~$2.40/mo (month 4+) residual
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Super Soldier "Power 2" Section */}
      <section className="py-20 bg-gradient-to-br from-purple-950/30 via-zinc-900 to-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              EXECUTIVE TIER - LIMITED TO 2 NATIONWIDE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">The </span>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent">&quot;Power 2&quot;</span>
              <span className="text-white"> Super Soldier</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-3xl mx-auto">
              An elite executive position for proven leaders. Each Super Soldier manages a 25-Soldier Platoon 
              and earns management overrides on every stylist activation.
            </p>
          </div>

          {/* Super Soldier Compensation Table */}
          <Card className="bg-zinc-950/80 border-purple-500/30 mb-8">
            <CardHeader>
              <CardTitle className="text-purple-400 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Super Soldier Compensation Formula
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Super Soldiers earn management overrides on every stylist activated by their platoon (after 30-day trial + 60-day retention).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-purple-400 font-semibold">Tier</th>
                      <th className="text-center py-3 px-4 text-zinc-400">Month 1 Override</th>
                      <th className="text-center py-3 px-4 text-zinc-400">Month 2 Override</th>
                      <th className="text-center py-3 px-4 text-green-400">Total Payout</th>
                      <th className="text-center py-3 px-4 text-amber-400">Permanent (3%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-zinc-800/50">
                      <td className="py-3 px-4 text-white font-medium">PRO ($29.99)</td>
                      <td className="py-3 px-4 text-center text-zinc-300">$2.50 held</td>
                      <td className="py-3 px-4 text-center text-zinc-300">$2.50 released</td>
                      <td className="py-3 px-4 text-center text-green-400 font-bold">$5.00</td>
                      <td className="py-3 px-4 text-center text-amber-400">$0.90/mo</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-white font-medium">ELITE ($49.99)</td>
                      <td className="py-3 px-4 text-center text-zinc-300">$3.50 held</td>
                      <td className="py-3 px-4 text-center text-zinc-300">$3.50 released</td>
                      <td className="py-3 px-4 text-center text-green-400 font-bold">$7.00</td>
                      <td className="py-3 px-4 text-center text-amber-400">$1.50/mo</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-zinc-600 mt-3">
                * Referrals start with 30-day FREE trial, then intro pricing for 3 months. Payouts begin after trial period.
              </p>
            </CardContent>
          </Card>

          {/* Executive Payout Milestones */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { 
                year: 'Year 1', 
                seats: '12,500 Seats',
                range: '$11,250 - $18,750',
                desc: '500 seats/Soldier × 25 Soldiers',
                color: 'from-blue-500 to-cyan-500'
              },
              { 
                year: 'Year 2', 
                seats: '25,000 Seats',
                range: '$22,500 - $37,500',
                desc: '1,000 seats/Soldier × 25 Soldiers',
                color: 'from-purple-500 to-pink-500'
              },
              { 
                year: 'Year 3', 
                seats: '37,500 Seats',
                range: '$33,750 - $56,250',
                desc: '1,500 seats/Soldier × 25 Soldiers',
                color: 'from-amber-500 to-yellow-500'
              }
            ].map((milestone, i) => (
              <Card key={i} className="bg-zinc-950 border-zinc-800 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${milestone.color}`} />
                <CardContent className="p-6 text-center">
                  <div className="text-sm text-zinc-500 mb-1">{milestone.year}</div>
                  <div className="text-lg font-bold text-white mb-2">{milestone.seats}</div>
                  <div className={`text-2xl font-bold bg-gradient-to-r ${milestone.color} bg-clip-text text-transparent mb-2`}>
                    {milestone.range}
                  </div>
                  <div className="text-xs text-zinc-500">/month in Management Residuals</div>
                  <div className="text-xs text-zinc-600 mt-2">{milestone.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Super Soldier KPIs */}
          <Card className="bg-zinc-950/80 border-purple-500/30 mb-8">
            <CardHeader>
              <CardTitle className="text-purple-400 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Key Performance Indicators (KPIs)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { title: 'Platoon Recruitment', desc: 'Successfully vet and onboard 25 Soldiers', icon: Users },
                  { title: '60-Day Rule', desc: 'Maintain 85%+ Month 2 retention rate across platoon', icon: Clock },
                  { title: 'Strategic Reporting', desc: 'Monthly "Platoon Health Report" to CEO', icon: TrendingUp },
                  { title: 'Active Management', desc: 'Intervene when retention drops below threshold', icon: Shield }
                ].map((kpi, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <kpi.icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{kpi.title}</h4>
                      <p className="text-sm text-zinc-400">{kpi.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Golden Handcuff Clause */}
          <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/30">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5" />
                The "Golden Handcuff" Clause
              </h3>
              <p className="text-zinc-300">
                The <strong className="text-white">3% Management Override is a lifetime residual</strong> as long as the Super Soldier 
                remains in an active management role. If the Super Soldier resigns or is removed for performance failure, 
                the 3% override reverts to the Company (GoNowBaby Media Group LLC), protecting the Founder's 77% gross margin.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Become a <span className="text-amber-400">Founding Partner</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Award, title: 'Founding Status', desc: 'Be among the first 50 salon owners in our exclusive partner program. Early adopters get the best perks.' },
              { icon: DollarSign, title: 'Passive Income', desc: 'Build a recurring revenue stream by helping fellow beauty professionals discover MyHairChair.' },
              { icon: Shield, title: 'Priority Support', desc: 'Get direct access to our team for any issues. Soldiers get VIP treatment and faster response times.' },
              { icon: Star, title: 'Exclusive Features', desc: 'Early access to new features before they launch publicly. Help shape the platform.' },
              { icon: Sparkles, title: '🤖 AI-Powered Tools', desc: 'NEW: AI Receipt Verification automatically validates payment screenshots. Prepay controls reduce no-shows.' },
              { icon: TrendingUp, title: 'Instant Payouts', desc: 'Via Stripe Express - get paid directly to your bank account with automatic W-9 handling.' }
            ].map((benefit, i) => (
              <Card key={i} className="bg-zinc-900/50 border-zinc-800 hover:border-amber-500/30 transition-all">
                <CardContent className="p-6">
                  <benefit.icon className="w-10 h-10 text-amber-400 mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-zinc-400 text-sm">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Eligibility Requirements
            </h2>
            <p className="text-zinc-400 mb-6">To ensure quality, we verify all applicants</p>
            
            {/* Apply Now Button at Top */}
            <Button 
              size="lg"
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-12 py-6 text-lg"
              data-testid="apply-now-top-btn"
            >
              Apply Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-6 sm:p-8">
              <ul className="space-y-4">
                {[
                  'Must be a verified salon or barbershop owner',
                  'Active business license (proof required)',
                  'At least 1 year in business',
                  'Minimum 1 stylist chair in operation',
                  'Active social media presence preferred',
                  'Commitment to ethical recruiting practices'
                ].map((req, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-zinc-300">{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Join the Ranks?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Only {spotsRemaining} founding spots remaining. Apply now and start earning.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-12 py-6 text-lg"
              data-testid="apply-now-bottom-btn"
            >
              Apply Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowShareModal(true)}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 font-bold px-8 py-6 text-lg"
              data-testid="share-bottom-btn"
            >
              <Share2 className="mr-2 w-5 h-5" />
              Share & Earn
            </Button>
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-amber-400" />
                    Founding Partner Application
                  </CardTitle>
                  <p className="text-zinc-400 text-sm mt-1">Complete all fields to apply as a HairChair Soldier</p>
                </div>
                <button 
                  onClick={() => setShowForm(false)}
                  className="text-zinc-400 hover:text-white p-2"
                >
                  ✕
                </button>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Personal Information
                  </h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Your Full Name *</label>
                      <Input
                        name="owner_name"
                        value={formData.owner_name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Smith"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="owner-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Email *</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="owner@salon.com"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Phone *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="(555) 123-4567"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                {/* Business Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Business Information
                  </h3>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Business/Salon Name *</label>
                    <Input
                      name="salon_name"
                      value={formData.salon_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Elite Cuts Barbershop"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="salon-name-input"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Years in Business *</label>
                      <Input
                        name="years_in_business"
                        type="number"
                        min="1"
                        value={formData.years_in_business}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="years-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Number of Stylists/Barbers *</label>
                      <Input
                        name="num_stylists"
                        type="number"
                        min="1"
                        value={formData.num_stylists}
                        onChange={handleInputChange}
                        required
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="num-stylists-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Number of Chairs *</label>
                    <Input
                      name="num_chairs"
                      type="number"
                      min="1"
                      value={formData.num_chairs}
                      onChange={handleInputChange}
                      required
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="num-chairs-input"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Business Address
                  </h3>
                  
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Street Address"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="address-input"
                  />
                  
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="City"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="city-input"
                    />
                    <Input
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="State"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="state-input"
                    />
                    <Input
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      required
                      placeholder="ZIP"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="zip-input"
                    />
                  </div>
                </div>

                {/* Social Media Presence */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <Instagram className="w-5 h-5" />
                    Social Media Presence
                  </h3>
                  <p className="text-sm text-zinc-500">Provide at least one social media account</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Instagram Handle</label>
                      <Input
                        name="instagram_handle"
                        value={formData.instagram_handle}
                        onChange={handleInputChange}
                        placeholder="@yoursalon"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="instagram-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Facebook Page URL</label>
                      <Input
                        name="facebook_url"
                        value={formData.facebook_url}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/yoursalon"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="facebook-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">TikTok Handle</label>
                      <Input
                        name="tiktok_handle"
                        value={formData.tiktok_handle}
                        onChange={handleInputChange}
                        placeholder="@yoursalon"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="tiktok-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Website</label>
                      <Input
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="https://yoursalon.com"
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="website-input"
                      />
                    </div>
                  </div>
                </div>

                {/* License Upload */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Business License (Required)
                  </h3>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">License Upload URL *</label>
                    <Input
                      name="license_upload_url"
                      value={formData.license_upload_url}
                      onChange={handleInputChange}
                      required
                      placeholder="https://drive.google.com/... or Dropbox link"
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="license-url-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Upload your business license to Google Drive, Dropbox, or any file sharing service and paste the link here
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Additional Proof (Optional)</label>
                    <Input
                      name="proof_of_business_url"
                      value={formData.proof_of_business_url}
                      onChange={handleInputChange}
                      placeholder="Link to salon photos, Google Business listing, etc."
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>

                {/* Why Join */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400">Why do you want to join HairChair Soldiers? *</h3>
                  <Textarea
                    name="why_join"
                    value={formData.why_join}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about yourself, your salon journey, and why you'd be a great HairChair Soldier. What excites you about this opportunity?"
                    rows={4}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="why-join-input"
                  />
                </div>

                {/* Eligibility Confirmations */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Eligibility Confirmation
                  </h3>
                  <p className="text-sm text-zinc-500">Please confirm you meet all requirements</p>
                  
                  <div className="space-y-3 bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_verified_owner"
                        checked={formData.is_verified_owner}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_verified_owner: e.target.checked }))}
                        required
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">I am a verified salon or barbershop owner *</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="has_active_license"
                        checked={formData.has_active_license}
                        onChange={(e) => setFormData(prev => ({ ...prev, has_active_license: e.target.checked }))}
                        required
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">I have an active business license (uploaded above) *</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="min_one_year"
                        checked={formData.min_one_year}
                        onChange={(e) => setFormData(prev => ({ ...prev, min_one_year: e.target.checked }))}
                        required
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">My business has been operating for at least 1 year *</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="min_one_chair"
                        checked={formData.min_one_chair}
                        onChange={(e) => setFormData(prev => ({ ...prev, min_one_chair: e.target.checked }))}
                        required
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">I have minimum 1 stylist chair in operation *</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="has_social_presence"
                        checked={formData.has_social_presence}
                        onChange={(e) => setFormData(prev => ({ ...prev, has_social_presence: e.target.checked }))}
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">I have an active social media presence (recommended)</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="commits_ethical_recruiting"
                        checked={formData.commits_ethical_recruiting}
                        onChange={(e) => setFormData(prev => ({ ...prev, commits_ethical_recruiting: e.target.checked }))}
                        required
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-zinc-300 text-sm">I commit to ethical recruiting practices *</span>
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-4 pt-4 border-t border-zinc-800">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1 border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold"
                    data-testid="submit-application-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 text-center text-zinc-500 text-sm">
          <p>© 2025 MyHairChair. HairChair Soldiers Founding Partner Program.</p>
          <p className="mt-2">Questions? Contact <a href="mailto:soldiers@myhairchair.com" className="text-amber-400 hover:underline">soldiers@myhairchair.com</a></p>
        </div>
      </footer>

      {/* Social Card Share Modal */}
      <SoldiersSocialCard 
        referralCode={myApplication?.application?.referral_code}
        userName={user?.name}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
}
